'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { currentPlayingIdAtom, type Messages } from '@/shared-ui';

import {
  HANDSFREE_POPUP_AUTO_CLOSE_MS,
  isAiSpeechStart,
  isEmbeddedScreenFocusTarget,
  shouldScheduleHandsFreePopupAutoClose,
} from './project-usability-handsfree-question-popup.helpers';

type ChatMessage = Messages[number];

interface QuestionRef {
  id: string;
}

interface UseHandsFreePopupLifecycleOptions {
  questions: ReadonlyArray<QuestionRef> | null | undefined;
  messages: ChatMessage[];
  isConversationOver: boolean;
  replyFeedbackIds: Set<string>;
}

export interface HandsFreePopupLifecycle {
  currentQuestionIndex: number;
  totalQuestions: number;
  isPopupOpen: boolean;
  togglePopup: () => void;
  openPopup: () => void;
  closePopup: () => void;
  hidePopup: () => void;
  onPopupMouseEnter: () => void;
  onPopupMouseLeave: () => void;
  lastAssistantMessage: ChatMessage | undefined;
  replyFeedbackMessage: ChatMessage | undefined;
  currentQuestionId: string | null;
  hasClosedTaskBar: boolean;
}

interface StableIndex {
  messageId: string | null;
  index: number;
}

function useStableQuestionIndex(
  lastAssistantMessage: ChatMessage | undefined,
  questions: ReadonlyArray<QuestionRef>
): number {
  const [stable, setStable] = useState<StableIndex>({
    messageId: null,
    index: 0,
  });

  const currentQuestionId = lastAssistantMessage?.questionId ?? null;
  const messageId = lastAssistantMessage?.id ?? null;

  const computedIndex =
    currentQuestionId && questions.length > 0
      ? questions.findIndex((q) => q.id === currentQuestionId)
      : -1;

  if (messageId && messageId !== stable.messageId && computedIndex !== -1) {
    setStable({ messageId, index: computedIndex });
    return computedIndex;
  }

  return computedIndex !== -1 ? computedIndex : stable.index;
}

// test-exception: requires @testing-library/react (renderHook) which is not installed in this repo; lifecycle helpers are covered by project-usability-handsfree-question-popup.helpers.spec.ts
export function useHandsFreePopupLifecycle({
  questions,
  messages,
  isConversationOver,
  replyFeedbackIds,
}: UseHandsFreePopupLifecycleOptions): HandsFreePopupLifecycle {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasClosedTaskBar, setHasClosedTaskBar] = useState(false);
  const [prevPlayingId, setPrevPlayingId] = useState<string | null>(null);
  const [prevTaskQuestionId, setPrevTaskQuestionId] = useState<string | null>(
    null
  );
  const [prevConversationOver, setPrevConversationOver] =
    useState(isConversationOver);

  const currentPlayingId = useAtomValue(currentPlayingIdAtom);

  const questionList = questions ?? [];
  const isSpeaking = currentPlayingId !== null;
  const totalQuestions = questionList.length;

  let lastAssistantIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sender === 'assistant') {
      lastAssistantIndex = i;
      break;
    }
  }

  const lastAssistantMessage =
    lastAssistantIndex >= 0 ? messages[lastAssistantIndex] : undefined;

  const precedingMessage =
    lastAssistantIndex > 0 ? messages[lastAssistantIndex - 1] : undefined;
  const replyFeedbackMessage =
    precedingMessage?.sender === 'assistant' &&
    replyFeedbackIds.has(precedingMessage.id)
      ? precedingMessage
      : undefined;

  const currentQuestionId = lastAssistantMessage?.questionId ?? null;
  const currentQuestionIndex = useStableQuestionIndex(
    lastAssistantMessage,
    questionList
  );

  if (currentQuestionId !== prevTaskQuestionId) {
    setPrevTaskQuestionId(currentQuestionId);
    setHasClosedTaskBar(false);
  }

  if (currentPlayingId !== prevPlayingId) {
    setPrevPlayingId(currentPlayingId);
    if (isAiSpeechStart(prevPlayingId, currentPlayingId)) {
      setIsPopupOpen(true);
    }
  }

  if (isConversationOver !== prevConversationOver) {
    setPrevConversationOver(isConversationOver);
    if (isConversationOver) {
      setIsPopupOpen(true);
    }
  }

  useEffect(() => {
    if (
      !shouldScheduleHandsFreePopupAutoClose({
        isPopupOpen,
        isSpeaking,
        isHovering,
        isConversationOver,
      })
    ) {
      return;
    }

    const timer = setTimeout(() => {
      setIsPopupOpen(false);
      setIsHovering(false);
    }, HANDSFREE_POPUP_AUTO_CLOSE_MS);

    return () => clearTimeout(timer);
  }, [isPopupOpen, isSpeaking, isHovering, isConversationOver]);

  useEffect(() => {
    if (!isPopupOpen) return;

    const handleWindowBlur = () => {
      if (isEmbeddedScreenFocusTarget(document.activeElement)) {
        setIsPopupOpen(false);
        setIsHovering(false);
        setHasClosedTaskBar(true);
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [isPopupOpen]);

  // Hide the popup without marking the task bar closed — used on pause so pausing never un-gates the task-completion button.
  const hidePopup = () => {
    setIsPopupOpen(false);
    setIsHovering(false);
  };

  const closePopup = () => {
    hidePopup();
    setHasClosedTaskBar(true);
  };

  const togglePopup = () => {
    setIsHovering(false);
    setIsPopupOpen((prev) => !prev);
  };

  const openPopup = () => {
    setIsHovering(false);
    setIsPopupOpen(true);
  };

  const onPopupMouseEnter = () => setIsHovering(true);
  const onPopupMouseLeave = () => setIsHovering(false);

  return {
    currentQuestionIndex,
    totalQuestions,
    isPopupOpen,
    togglePopup,
    openPopup,
    closePopup,
    hidePopup,
    onPopupMouseEnter,
    onPopupMouseLeave,
    lastAssistantMessage,
    replyFeedbackMessage,
    currentQuestionId,
    hasClosedTaskBar,
  };
}
